import { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Check } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { MultiSelect } from '@/components/MultiSelect';
import SelectTagInput from '@/components/SelectTagInput';
import { areasService } from '@/services/areas';
import { Area } from '@/types/areas';

import { BrandFormData, PhaseProps } from '@/pages/settings/content/brands-sectors/create/shared/types';

export default function One({ onNext }: PhaseProps) {
  const { register, watch, setValue, formState: { errors } } = useFormContext<BrandFormData>();
  
  const [areas, setAreas] = useState<Area[]>([]);
  const [loadingAreas, setLoadingAreas] = useState(false);

  const watchedData = watch();

  useEffect(() => {
    const loadAreas = async () => {
      setLoadingAreas(true);
      try {
        const response = await areasService.getAll();
        setAreas(response.data || []);
      } catch (error) {
        console.error('Erro ao carregar áreas:', error);
      } finally {
        setLoadingAreas(false);
      }
    };

    loadAreas();
  }, []);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Nova Marca</h2>
        <p className="text-gray-600">Adicione uma nova Marca ao Culture Monitor.</p>
      </div>

      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Informações Gerais</h3>
          <p className="text-sm text-gray-600 mb-6">Preencha os campos abaixo sobre a atuação da Marca.</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nome da Marca
            </label>
            <Input
              {...register('name')}
              placeholder="Nome da Marca"
              className={`w-full ${errors.name ? 'border-red-500' : ''}`}
            />
            {errors.name && (
              <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Setor</label>
            <MultiSelect
              value={watchedData.areas || []}
              onChange={(ids) => setValue('areas', ids, { shouldValidate: true })}
              items={areas}
              placeholder="Selecione o(s) setor(es)"
              loading={loadingAreas}
            />
            {errors.areas && (
              <p className="text-red-500 text-xs mt-1">{errors.areas.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Subsetor</label>
            <SelectTagInput
              value={watchedData.subsectors || []}
              onChange={(tags) => setValue('subsectors', tags, { shouldValidate: true })}
              placeholder="Defina os itens"
              showSearchIcon={true}
              items={[]}
              loading={false}
              hideButton
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Mercados Foco</label>
            <SelectTagInput
              value={watchedData.markets || []}
              onChange={(tags) => setValue('markets', tags, { shouldValidate: true })}
              placeholder="Defina os itens"
              showSearchIcon={true}
              items={[]}
              loading={false}
              hideButton
            />
            {errors.markets && (
              <p className="text-red-500 text-xs mt-1">{errors.markets.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Regionalização Adicional</label>
            <SelectTagInput
              value={watchedData.regionalization || []}
              onChange={(tags) => setValue('regionalization', tags, { shouldValidate: true })}
              placeholder="Defina os itens"
              showSearchIcon={true}
              items={[]}
              loading={false}
              hideButton
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Assinatura</label>
            <Select 
              value={watchedData.subscription} 
              onValueChange={(value: 'BASIC' | 'MEDIUM' | 'ADVANCED' | 'PRO') => setValue('subscription', value, { shouldValidate: true })}
            >
              <SelectTrigger className="w-full">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  <SelectValue placeholder="Tipo de Assinatura" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="BASIC">Basic</SelectItem>
                <SelectItem value="MEDIUM">Medium</SelectItem>
                <SelectItem value="ADVANCED">Advanced</SelectItem>
                <SelectItem value="PRO">Pro</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data de Início do Contrato</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                >
                  {watchedData.contractStartDate ? (
                    format(watchedData.contractStartDate, 'dd/MM/yy', { locale: ptBR })
                  ) : (
                    <span>dd/mm/aa</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={watchedData.contractStartDate}
                  onSelect={(date) => setValue('contractStartDate', date)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">Status:</label>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={watchedData.is_active}
                onChange={(e) => setValue('is_active', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
            </label>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={onNext}
          className="bg-gray-200 text-gray-800 hover:bg-gray-300 flex items-center gap-2"
        >
          Próximo
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Button>
      </div>
    </div>
  );
}
